import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { API_BASE_URL } from "../../config/api";

const AddCompany = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [airports, setAirports] = useState([]);
  const [loadingAirports, setLoadingAirports] = useState(true);

  // Fetch airports on component mount
  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_BASE_URL}/airport/all`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          setAirports(data.items);
        }
      } catch (err) {
        console.error("Error fetching airports:", err);
        setError("Failed to load airports");
      } finally {
        setLoadingAirports(false);
      }
    };

    fetchAirports();
  }, []);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("authToken");
      // Exclude selectedAirportId from submission as it's only for internal state
      const { selectedAirportId, ...submitValues } = values;
      const response = await fetch(`${API_BASE_URL}/company/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitValues),
      });

      const data = await response.json();

      if (!response.ok || data.subCode !== 200) {
        throw new Error(data.message || "Failed to add company");
      }

      setSuccess("Company added successfully!");
      // Optionally reset form or redirect
    } catch (err) {
      console.error("Add company error:", err);
      setError(err.message || "An error occurred while adding the company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="ADD COMPANY" subtitle="Add a new company to the system" />

      {error && (
        <Box
          color="error.main"
          p={2}
          mb={2}
          sx={{ backgroundColor: "rgba(211, 47, 47, 0.1)", borderRadius: "4px" }}
        >
          {error}
        </Box>
      )}

      {success && (
        <Box
          color="success.main"
          p={2}
          mb={2}
          sx={{ backgroundColor: "rgba(46, 125, 50, 0.1)", borderRadius: "4px" }}
        >
          {success}
        </Box>
      )}

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={companySchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Basic Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Company ID"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values._id}
                    name="_id"
                    error={!!touched._id && !!errors._id}
                    helperText={touched._id && errors._id}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Company Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    name="name"
                    error={!!touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Image"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.image}
                    name="image"
                    placeholder="e.g., TIDy-logo-1.webp"
                    sx={{ gridColumn: "span 4" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Airport Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Airport Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                    <InputLabel>Select Airport</InputLabel>
                    <Select
                      value={values.selectedAirportId || ""}
                      onChange={(e) => {
                        const selectedAirport = airports.find(
                          (airport) => airport.id === e.target.value
                        );
                        if (selectedAirport) {
                          setFieldValue("selectedAirportId", selectedAirport.id);
                          setFieldValue("airport_id", selectedAirport.id);
                          setFieldValue("airport_name", selectedAirport.name);
                        }
                      }}
                      label="Select Airport"
                      disabled={loadingAirports}
                    >
                      {loadingAirports ? (
                        <MenuItem value="">Loading airports...</MenuItem>
                      ) : airports.length === 0 ? (
                        <MenuItem value="">No airports available</MenuItem>
                      ) : (
                        airports.map((airport) => (
                          <MenuItem key={airport.id} value={airport.id}>
                            {airport.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Airport Name"
                    value={values.airport_name || ""}
                    name="airport_name"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Airport ID"
                    value={values.airport_id || ""}
                    name="airport_id"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ gridColumn: "span 1" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Company Details */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Company Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Registered Owner"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.registered_owner}
                    name="registered_owner"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Registration Number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.registration_no}
                    name="registration_no"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.address}
                    name="address"
                    multiline
                    rows={3}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Documents"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.documents}
                    name="documents"
                    placeholder="Document information"
                    sx={{ gridColumn: "span 4" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Contact Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Contact Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    name="email"
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Customer Support Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.customer_support_email}
                    name="customer_support_email"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Contact Number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.contact_number}
                    name="contact_number"
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Login Credentials */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Login Credentials
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Login"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.login}
                    name="login"
                    error={!!touched.login && !!errors.login}
                    helperText={touched.login && errors.login}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="password"
                    label="Password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    name="password"
                    error={!!touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Box display="flex" justifyContent="end" mt="20px" gap="10px">
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  // Reset form logic if needed
                }}
              >
                Reset
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loading}
              >
                {loading ? "Adding Company..." : "Add Company"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

// Validation schema
const companySchema = yup.object().shape({
  _id: yup.string().required("Company ID is required"),
  name: yup.string().required("Company name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  login: yup.string().required("Login is required"),
  password: yup.string().required("Password is required"),
});

// Initial values
const initialValues = {
  _id: "",
  name: "",
  image: "",
  airport_name: "",
  airport_id: "",
  registered_owner: "",
  registration_no: "",
  contact_number: "",
  email: "",
  customer_support_email: "",
  address: "",
  documents: "",
  login: "",
  password: "",
  selectedAirportId: "", // Internal state for airport selection
};

export default AddCompany;

